import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Users,
  ClipboardList,
  Gauge,
  Droplet,
  BarChart3,
  Scissors,
  TrendingDown,
  AlertTriangle,
  GraduationCap,
  FileBarChart2,
  DollarSign,
  CalendarDays,
  Home,
  MapPin,
  Network,
  Route,
  FileText,
  ScrollText,
  Settings,
  Headset,
  Activity,
  Warehouse,
  Package,
  Truck,
  Cog,
  Wrench,
  CalendarClock,
  ShoppingCart,
  SlidersHorizontal,
  ArrowLeft,
  KeyRound,
  LogOut,
  Menu,
  Search,
  ShieldCheck,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  PackageCheck,
  FileCheck2,
  Wallet,
  Receipt,
  Banknote,
  HandCoins,
  MessagesSquare,
  X,
  BookOpen,
  Calculator,
  FileUp,
  LayoutDashboard,
  ListTree,
  Table2,
  Grid3x3,
  Waves,
  FlaskConical,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";

import { useQuery } from "@tanstack/react-query";

import { AsistenteBoton } from "../features/asistente/AsistentePanel";
import { fetchDashboardOperativo } from "../features/dashboard/api";
import { useEstadoDte } from "../features/dte/api";
import { useAuth } from "../features/auth/useAuth";
import { hasPermission } from "../lib/auth";
import { formatRut } from "../lib/rut";
import { listarSocios } from "../features/socios/api";
import { AvisoAccesoOficina, ComiteSelector } from "./ComiteSelector";
import { ToastContainer } from "./Toast";
import {
  CommandPalette,
  useCommandPalette,
  type CommandItem,
  type FuenteRemota,
} from "./CommandPalette";

// Detección de plataforma para mostrar el atajo correcto del command
// palette (⌘ en Mac, Ctrl en Windows/Linux). Se evalúa una sola vez al
// cargar el módulo — los usuarios no cambian de OS a mitad de sesión.
const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPod|iPad/i.test(navigator.platform);

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Si está seteado, el item solo aparece si el usuario tiene este permiso. */
  permission?: string;
  /** Sinónimos del oficio para la búsqueda (Ctrl+K): "pago" → Caja. */
  keywords?: string[];
  /** Subtítulo chico que se dibuja ANTES de este ítem dentro del grupo
   *  (separa el ciclo de los parámetros, NAV-04). */
  subtitulo?: string;
  /** Sólo en comités que emiten documentos tributarios electrónicos (DTE C.5). */
  requiereDte?: boolean;
}

interface NavGroup {
  id: string;
  label: string;
  /** Ícono del módulo: encabeza el grupo en el menú expandido. */
  icon: LucideIcon;
  /** Sección del menú (ver SECTIONS). Administración va al pie, sin sección. */
  section?: SectionId;
  items: NavItem[];
  tone?: "brand" | "amber";
}

type SectionId = "operacion" | "servicio" | "gestion";

// Secciones por finalidad (rediseño 2026-10-14): agrupan los módulos sin
// cambiarles nombre ni contenido. Administración vive en el pie del sidebar.
const SECTIONS: { id: SectionId; label: string }[] = [
  { id: "operacion", label: "Operación" },
  { id: "servicio", label: "Servicio y cumplimiento" },
  { id: "gestion", label: "Gestión interna" },
];

// Menú definitivo (auditoría de diseño 2026-10): orden por el ciclo del mes,
// sinónimos del oficio en `keywords` para la búsqueda (Ctrl+K) y Continuidad
// abierto en sus cuatro pestañas. El título de cada pantalla debe coincidir
// con el `label` de su ítem (NAV-05).
const GROUPS: NavGroup[] = [
  {
    id: "padron",
    label: "Socios y red",
    icon: Users,
    section: "operacion",
    items: [
      {
        to: "/socios",
        label: "Socios",
        icon: Users,
        permission: "socios.read",
        keywords: ["padrón", "socio", "cliente", "rut", "cuenta corriente"],
      },
      {
        to: "/sectores",
        label: "Sectores",
        icon: MapPin,
        permission: "sectores.read",
        keywords: ["sector", "zona"],
      },
      {
        to: "/rutas",
        label: "Rutas",
        icon: Route,
        permission: "rutas.read",
        keywords: ["ruta", "recorrido", "operador", "terreno"],
      },
      {
        to: "/arranques",
        label: "Arranques",
        icon: Network,
        permission: "arranques.read",
        keywords: ["arranque", "conexión", "mapa", "domicilio"],
      },
      {
        to: "/medidores",
        label: "Medidores",
        icon: Gauge,
        permission: "medidores.read",
        keywords: ["medidor", "instalación", "retiro"],
      },
      {
        to: "/contratos",
        label: "Contratos",
        icon: FileText,
        permission: "contratos.read",
        keywords: ["contrato", "traspaso", "titular", "conexión nueva", "herencia"],
      },
    ],
  },
  {
    id: "facturacion",
    label: "Facturación",
    icon: Receipt,
    section: "operacion",
    // Orden del ciclo (NAV-04): leer → revisar → cerrar y emitir → repartir.
    // Lo que se configura o analiza va después, bajo su subtítulo.
    items: [
      {
        to: "/lecturas",
        label: "Lecturas",
        icon: Gauge,
        permission: "lecturas.read",
        keywords: ["medidor", "consumo", "lectura", "término medio", "digitar"],
      },
      {
        to: "/consumos",
        label: "Revisión de consumos",
        icon: Droplet,
        permission: "lecturas.read",
        keywords: ["consumo alto", "anomalía", "fuga", "revisar"],
      },
      {
        to: "/cierres",
        label: "Cierres",
        icon: ClipboardList,
        permission: "cierres.read",
        keywords: ["facturar", "emitir", "boletas", "cierre del mes", "período", "imprimir"],
      },
      {
        to: "/documentos-sii",
        label: "Documentos SII",
        icon: FileCheck2,
        permission: "cierres.read",
        requiereDte: true,
        keywords: ["sii", "dte", "folio", "boleta electrónica", "nota de crédito", "rechazada"],
      },
      {
        to: "/entregas",
        label: "Entrega de boletas",
        icon: PackageCheck,
        permission: "entregas.read",
        keywords: ["reparto", "entrega", "boletas", "ronda"],
      },
      {
        to: "/tarifas",
        label: "Tarifas",
        icon: DollarSign,
        permission: "tarifas.read",
        subtitulo: "Parámetros y análisis",
        keywords: ["precio", "tramo", "cargo fijo", "valor del m3"],
      },
      {
        to: "/reajustes",
        label: "Reajuste tarifario",
        icon: FileBarChart2,
        permission: "tarifas.read",
        keywords: ["ipc", "reajuste", "siss"],
      },
      {
        to: "/analisis-consumo",
        label: "Análisis de consumo",
        icon: BarChart3,
        permission: "lecturas.read",
        keywords: ["agua no facturada", "anf", "estadística", "producción"],
      },
    ],
  },
  {
    id: "cobranza",
    label: "Cobranza",
    icon: Banknote,
    section: "operacion",
    items: [
      {
        to: "/caja",
        label: "Caja del día",
        icon: Wallet,
        permission: "pagos.read",
        keywords: ["pago", "cobro", "recaudación", "arqueo", "caja", "recibo"],
      },
      {
        to: "/morosidad",
        label: "Morosidad",
        icon: TrendingDown,
        permission: "cortes.read",
        keywords: ["deuda", "deudores", "morosos", "saldo"],
      },
      {
        to: "/cortes",
        label: "Cortes",
        icon: Scissors,
        permission: "cortes.read",
        keywords: ["corte", "reposición", "suspensión", "orden de corte"],
      },
      {
        to: "/multas",
        label: "Multas",
        icon: AlertTriangle,
        permission: "multas.read",
        keywords: ["multa", "sanción", "inasistencia"],
      },
    ],
  },
  {
    id: "subsidios",
    label: "Subsidios",
    icon: HandCoins,
    section: "operacion",
    items: [
      {
        to: "/subsidios",
        label: "Subsidios",
        icon: GraduationCap,
        permission: "subsidios.read",
        keywords: ["subsidio", "beneficio", "rsh"],
      },
      {
        to: "/subsidios/organismos",
        label: "Organismos",
        icon: Building2,
        permission: "subsidios.gestionar",
        keywords: ["municipalidad", "organismo"],
      },
      {
        to: "/subsidios/rendicion",
        label: "Rendición FNDR",
        icon: FileBarChart2,
        permission: "subsidios.gestionar",
        keywords: ["rendición", "fndr", "recupero"],
      },
    ],
  },
  {
    id: "comunidad",
    label: "Atención al usuario",
    icon: MessagesSquare,
    section: "servicio",
    items: [
      {
        to: "/atenciones",
        label: "Atenciones",
        icon: Headset,
        permission: "atenciones.read",
        keywords: ["reclamo", "solicitud", "consulta", "emergencia", "libro de reclamos"],
      },
    ],
  },
  {
    id: "continuidad",
    label: "Continuidad y planta",
    icon: Activity,
    section: "servicio",
    // Las cuatro pestañas de /continuidad, cada una con su enlace (NAV-08).
    items: [
      {
        to: "/continuidad?tab=agua",
        label: "Agua potable",
        icon: Droplet,
        permission: "continuidad.read",
        keywords: ["interrupción", "corte de agua", "producción", "continuidad", "siss"],
      },
      {
        to: "/continuidad?tab=alcantarillado",
        label: "Alcantarillado",
        icon: Waves,
        permission: "continuidad.read",
        keywords: ["obstrucción", "afloramiento", "colector"],
      },
      {
        to: "/continuidad?tab=presiones",
        label: "Presiones",
        icon: Gauge,
        permission: "continuidad.read",
        keywords: ["presión", "pcp", "mca"],
      },
      {
        to: "/continuidad?tab=plantas",
        label: "Planta y desinfección",
        icon: FlaskConical,
        permission: "continuidad.read",
        keywords: ["cloro", "desinfección", "planta", "pozo"],
      },
    ],
  },
  {
    id: "asambleas",
    label: "Directiva y asambleas",
    icon: CalendarDays,
    section: "gestion",
    items: [
      {
        to: "/asambleas",
        label: "Asambleas",
        icon: CalendarDays,
        permission: "asambleas.read",
        keywords: ["reunión", "asistencia", "quórum", "directiva", "acta"],
      },
    ],
  },
  {
    id: "contabilidad",
    label: "Contabilidad",
    icon: Calculator,
    section: "gestion",
    items: [
      {
        to: "/contabilidad",
        label: "Resumen contable",
        icon: LayoutDashboard,
        permission: "contabilidad.read",
        keywords: ["indicadores", "contabilidad"],
      },
      {
        to: "/contabilidad/balance",
        label: "Balance 8 columnas",
        icon: Table2,
        permission: "contabilidad.read",
        keywords: ["balance"],
      },
      {
        to: "/contabilidad/cuentas-por-mes",
        label: "Cuentas por mes",
        icon: Grid3x3,
        permission: "contabilidad.read",
        keywords: ["matriz", "mensual"],
      },
      {
        to: "/contabilidad/mayor",
        label: "Libro mayor",
        icon: BookOpen,
        permission: "contabilidad.read",
        keywords: ["mayor", "cuenta"],
      },
      {
        to: "/contabilidad/diario",
        label: "Libro diario",
        icon: Calculator,
        permission: "contabilidad.read",
        keywords: ["diario", "comprobante", "asiento"],
      },
      {
        to: "/contabilidad/cargas",
        label: "Cargas y publicación",
        icon: FileUp,
        permission: "contabilidad.cargar",
        keywords: ["subir", "excel", "publicar"],
      },
      {
        to: "/contabilidad/plan",
        label: "Plan de cuentas",
        icon: ListTree,
        permission: "contabilidad.plan",
        keywords: ["cuentas", "clasificación"],
      },
    ],
  },
  {
    id: "inventario",
    label: "Bodega e inventario",
    icon: Warehouse,
    section: "gestion",
    items: [
      {
        to: "/bodega",
        label: "Bodega",
        icon: Warehouse,
        permission: "inventario.read",
        keywords: ["inventario", "stock", "existencias", "movimiento"],
      },
      {
        to: "/insumos",
        label: "Insumos",
        icon: Package,
        permission: "inventario.read",
        keywords: ["material", "repuesto", "insumo"],
      },
      {
        to: "/proveedores",
        label: "Proveedores",
        icon: Truck,
        permission: "inventario.read",
        keywords: ["proveedor"],
      },
    ],
  },
  {
    id: "mantenimiento",
    label: "Mantenimiento",
    icon: Wrench,
    section: "gestion",
    items: [
      {
        to: "/activos",
        label: "Activos",
        icon: Cog,
        permission: "mantenimiento.read",
        keywords: ["equipo", "activo", "bomba", "levantamiento"],
      },
      {
        to: "/ordenes-trabajo",
        label: "Órdenes de trabajo",
        icon: Wrench,
        permission: "mantenimiento.read",
        keywords: ["ot", "orden de trabajo", "reparación", "falla"],
      },
      {
        to: "/planes-mantencion",
        label: "Planes de mantención",
        icon: CalendarClock,
        permission: "mantenimiento.read",
        keywords: ["preventivo", "mantención", "plan"],
      },
    ],
  },
  {
    id: "compras",
    label: "Compras",
    icon: ShoppingCart,
    section: "gestion",
    items: [
      {
        to: "/compras/solicitudes",
        label: "Solicitudes (SIC)",
        icon: ClipboardList,
        permission: "compras.read",
        keywords: ["sic", "solicitud de compra", "ratificar"],
      },
      {
        to: "/compras/ordenes",
        label: "Órdenes de compra",
        icon: ShoppingCart,
        permission: "compras.read",
        keywords: ["oc", "orden de compra", "recepción"],
      },
      {
        to: "/compras/configuracion",
        label: "Política de compras",
        icon: SlidersHorizontal,
        permission: "compras.configurar",
        keywords: ["política", "montos", "aprobación"],
      },
    ],
  },
  {
    id: "administracion",
    label: "Administración",
    icon: Settings,
    items: [
      {
        to: "/configuracion",
        label: "Configuración",
        icon: Settings,
        permission: "empresa.configurar",
        keywords: ["comité", "datos", "banco", "boleta", "ciclo", "ajustes"],
      },
      {
        to: "/auditoria",
        label: "Auditoría",
        icon: ScrollText,
        permission: "audit.read",
        keywords: ["bitácora", "registro", "historial", "quién"],
      },
    ],
  },
];

const SUPERADMIN_GROUP: NavGroup = {
  id: "superadmin",
  label: "Operador SaaS",
  icon: ShieldCheck,
  tone: "amber",
  items: [
    { to: "/superadmin/empresas", label: "Comités", icon: Building2 },
    { to: "/superadmin/roles", label: "Roles y permisos", icon: ShieldCheck },
  ],
};

const GROUPS_COLLAPSED_KEY = "innovasuite.sidebar.groups-collapsed";
const SIDEBAR_COLLAPSED_KEY = "innovasuite.sidebar.collapsed";

// Todos los ids de grupo posibles (incl. Console). Ids que no correspondan
// a un grupo visible del usuario quedan en el set pero son inofensivos.
const ALL_GROUP_IDS: string[] = [...GROUPS.map((g) => g.id), SUPERADMIN_GROUP.id];

// Primer ingreso (NAV-12): abiertos los grupos del trabajo de todos los días.
const GRUPOS_ABIERTOS_POR_DEFECTO = new Set(["facturacion", "cobranza"]);

function readGroupsCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(GROUPS_COLLAPSED_KEY);
    // Primer ingreso (sin preferencia guardada): Facturación y Cobranza
    // abiertos, el resto colapsado. El efecto de `activeGroupId` expande
    // además el de la ruta actual. Una vez que hay preferencia, se respeta.
    if (raw === null) {
      return new Set(ALL_GROUP_IDS.filter((id) => !GRUPOS_ABIERTOS_POR_DEFECTO.has(id)));
    }
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((x): x is string => typeof x === "string"));
    }
    return new Set();
  } catch {
    return new Set();
  }
}

function writeGroupsCollapsed(set: Set<string>): void {
  try {
    localStorage.setItem(GROUPS_COLLAPSED_KEY, JSON.stringify([...set]));
  } catch {
    // localStorage puede fallar en modo privado; degradamos en silencio.
  }
}

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(value: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, value ? "1" : "0");
  } catch {
    // ignore
  }
}

const DESKTOP_QUERY = "(min-width: 768px)"; // breakpoint `md` de Tailwind

function useIsDesktop(): boolean {
  const soportado =
    typeof window !== "undefined" && typeof window.matchMedia === "function";
  const [isDesktop, setIsDesktop] = useState(
    () => !soportado || window.matchMedia(DESKTOP_QUERY).matches,
  );
  useEffect(() => {
    if (!soportado) return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (): void => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [soportado]);
  return isDesktop;
}

/** Ruta sin query string (`/continuidad?tab=agua` → `/continuidad`). */
function rutaDe(to: string): string {
  return to.split("?")[0] ?? to;
}

/** ¿La URL actual cae dentro de la pantalla del ítem? Ignora el query:
 *  sirve para saber qué GRUPO contiene la ruta. */
function isItemActive(pathname: string, to: string): boolean {
  const ruta = rutaDe(to);
  return pathname === ruta || pathname.startsWith(`${ruta}/`);
}

/** ¿Este ítem es el activo? Como NavLink, más dos casos:
 *  - con un hermano que es sub-ruta suya (`/subsidios` y
 *    `/subsidios/rendicion`) exige match exacto;
 *  - con `?tab=` (Continuidad) compara la pestaña; sin `tab` en la URL, la
 *    activa es la del primer ítem que apunta a esa ruta. */
function itemActivo(
  item: NavItem,
  items: NavItem[],
  pathname: string,
  search: string,
): boolean {
  const ruta = rutaDe(item.to);
  const exacto = items.some(
    (o) => o.to !== item.to && rutaDe(o.to).startsWith(`${ruta}/`),
  );
  const enRuta = exacto
    ? pathname === ruta
    : pathname === ruta || pathname.startsWith(`${ruta}/`);
  if (!enRuta) return false;
  const tabItem = new URLSearchParams(item.to.split("?")[1] ?? "").get("tab");
  if (tabItem === null) return true;
  const primero = items.find((o) => rutaDe(o.to) === ruta);
  const tabPorDefecto = primero
    ? new URLSearchParams(primero.to.split("?")[1] ?? "").get("tab")
    : null;
  const tabActual = new URLSearchParams(search).get("tab") ?? tabPorDefecto;
  return tabItem === tabActual;
}

/** Label del item activo del grupo (o null): el header del grupo lo usa para
 *  indicar dónde está el usuario aunque el grupo esté colapsado. */
function activeItemLabel(group: NavGroup, pathname: string, search: string): string | null {
  return (
    group.items.find((it) => itemActivo(it, group.items, pathname, search))?.label ?? null
  );
}

/** Iniciales para el avatar del footer, derivadas del nombre del comité
 *  (antes se usaba el `empresa_id`, que mostraba "1"). Toma la inicial de
 *  las primeras dos palabras; con una sola palabra, sus dos primeras letras. */
function empresaInitials(nombre: string): string {
  const words = nombre.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0] ?? "";
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  const second = words[1] ?? "";
  return (first.slice(0, 1) + second.slice(0, 1)).toUpperCase();
}

export function AdminLayout(): JSX.Element {
  const { state, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(() =>
    readGroupsCollapsed(),
  );
  const [collapsedPref, setSidebarCollapsed] = useState<boolean>(() =>
    readSidebarCollapsed(),
  );
  // El modo contraído es sólo de escritorio: en móvil el drawer se abre
  // siempre completo aunque la preferencia guardada sea "contraído".
  const isDesktop = useIsDesktop();
  const sidebarCollapsed = collapsedPref && isDesktop;
  const { open: cmdOpen, setOpen: setCmdOpen } = useCommandPalette();

  const toggleGroup = (id: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeGroupsCollapsed(next);
      return next;
    });
  };

  const toggleSidebar = (): void => {
    setSidebarCollapsed((prev) => {
      writeSidebarCollapsed(!prev);
      return !prev;
    });
  };

  // Filtra cada grupo a los items que el usuario puede ver. Si un grupo
  // queda sin items (todos requieren un permiso que no tiene), se descarta.
  const dteHabilitado = useEstadoDte().data?.habilitado ?? false;
  const visibleGroups = useMemo(() => {
    return GROUPS.map((g) => ({
      ...g,
      items: g.items.filter(
        (it) =>
          (!it.permission || hasPermission(state, it.permission)) &&
          (!it.requiereDte || dteHabilitado),
      ),
    })).filter((g) => g.items.length > 0);
  }, [state, dteHabilitado]);

  const allGroups = state?.isSuperadmin
    ? [...visibleGroups, SUPERADMIN_GROUP]
    : visibleGroups;
  const activeGroup = allGroups.find((g) =>
    g.items.some((it) => isItemActive(location.pathname, it.to)),
  );
  const activeGroupId = activeGroup?.id;

  useEffect(() => {
    if (!activeGroupId) return;
    setCollapsed((prev) => {
      if (!prev.has(activeGroupId)) return prev;
      const next = new Set(prev);
      next.delete(activeGroupId);
      writeGroupsCollapsed(next);
      return next;
    });
  }, [activeGroupId]);

  // Auto-scroll al item activo dentro del sidebar. Con ~23 items el nav
  // scrollea; al entrar por URL directa / F5 el item de la ruta puede quedar
  // fuera de vista. Lo traemos a la vista (block:"nearest" = mínimo scroll).
  // El rAF espera a que el grupo activo termine de expandirse.
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const active = navRef.current?.querySelector('[aria-current="page"]');
      active?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.pathname, location.search]);

  // Drawer móvil: cerrado queda inerte (sus links no se tabulan aunque sólo
  // esté desplazado fuera de pantalla); al abrir, el foco entra al botón de
  // cerrar y al cerrar vuelve a la hamburguesa.
  const asideRef = useRef<HTMLElement>(null);
  const hamburguesaRef = useRef<HTMLButtonElement>(null);
  const cerrarDrawerRef = useRef<HTMLButtonElement>(null);
  const drawerAbiertoAntes = useRef(false);
  useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return;
    if (!isDesktop && !mobileOpen) aside.setAttribute("inert", "");
    else aside.removeAttribute("inert");
    if (isDesktop) return;
    if (mobileOpen) cerrarDrawerRef.current?.focus();
    else if (drawerAbiertoAntes.current) hamburguesaRef.current?.focus();
    drawerAbiertoAntes.current = mobileOpen;
  }, [mobileOpen, isDesktop]);

  // Escape cierra el drawer móvil.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // Contadores del menú: las alertas críticas/altas del Inicio, asignadas al
  // módulo dueño de su pantalla (href). Reusa la query del dashboard (misma
  // key → mismo cache) y sólo si el usuario puede verlo.
  const puedeVerDashboard = hasPermission(state, "socios.read");
  const dashQ = useQuery({
    queryKey: ["dashboard", "operativo"],
    queryFn: fetchDashboardOperativo,
    // Los contadores del menú no necesitan estar al minuto: el dashboard es
    // la consulta más pesada del admin y el menú está en todas las pantallas.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: puedeVerDashboard,
  });
  const alertasPorGrupo = useMemo(() => {
    const out = new Map<string, number>();
    for (const a of dashQ.data?.alertas ?? []) {
      if (a.severidad !== "critica" && a.severidad !== "alta") continue;
      const path = a.href.split("?")[0] ?? a.href;
      const g = visibleGroups.find((grp) =>
        grp.items.some((it) => isItemActive(path, it.to)),
      );
      if (g) out.set(g.id, (out.get(g.id) ?? 0) + (a.cantidad ?? 1));
    }
    return out;
  }, [dashQ.data, visibleGroups]);

  // Tooltip del modo contraído: se dibuja fuera del <nav> (que scrollea y
  // recortaría un tooltip absoluto). Aparece con hover y con foco de teclado;
  // cada link además lleva aria-label, así que el tooltip no es la única
  // forma de identificarlo.
  const [tooltip, setTooltip] = useState<{ label: string; top: number } | null>(
    null,
  );
  const tooltipHandlers = sidebarCollapsed
    ? {
        onShow: (label: string, el: HTMLElement) => {
          const r = el.getBoundingClientRect();
          setTooltip({ label, top: r.top + r.height / 2 });
        },
        onHide: () => setTooltip(null),
      }
    : null;
  useEffect(() => setTooltip(null), [sidebarCollapsed, location.pathname]);

  const adminGroup = visibleGroups.find((g) => g.section === undefined);

  // Nombre del comité para el footer del sidebar (mismo criterio que el
  // topbar): comité visitado en login-as > claim empresa_nombre > fallback.
  const empresaLabel = state
    ? state.viaLoginAs && state.empresaTargetNombre
      ? state.empresaTargetNombre
      : state.empresaNombre ?? `Empresa #${state.empresaId}`
    : "";

  // Cuando el sidebar se colapsa (icon-only mode), todos los grupos
  // deben mostrar sus items — los headers de grupo desaparecen, así
  // que la única forma de acceder es directo por el icono.
  const expandedGroup = (groupId: string): boolean =>
    sidebarCollapsed || !collapsed.has(groupId);

  // Items del Command Palette — derivados de los grupos visibles +
  // permisos + acciones de sesión. Cmd+K abre el palette desde
  // cualquier pantalla del admin.
  const commandItems = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [
      {
        id: "/",
        label: "Inicio",
        description: "Dashboard contextual",
        icon: Home,
        category: "Pantallas",
        keywords: ["dashboard", "home"],
        perform: () => navigate("/"),
      },
    ];
    for (const group of visibleGroups) {
      for (const item of group.items) {
        items.push({
          id: item.to,
          label: item.label,
          description: group.label,
          icon: item.icon,
          category: "Pantallas",
          keywords: [group.label, ...(item.keywords ?? [])],
          perform: () => navigate(item.to),
        });
      }
    }
    if (state?.isSuperadmin) {
      for (const item of SUPERADMIN_GROUP.items) {
        items.push({
          id: `console:${item.to}`,
          label: item.label,
          description: "Console SaaS",
          icon: item.icon,
          category: "Console",
          keywords: ["console", "saas", "operador", "superadmin"],
          perform: () => navigate(item.to),
        });
      }
    }
    if (state?.viaLoginAs) {
      items.push({
        id: "back-to-console",
        label: "Volver a Console SaaS",
        description: "Salir del modo soporte",
        icon: ArrowLeft,
        category: "Acciones",
        keywords: ["console", "soporte", "salir"],
        perform: () => navigate("/superadmin/empresas"),
      });
    }
    items.push({
      id: "logout",
      label: "Cerrar sesión",
      description: "Salir del panel administrativo",
      icon: LogOut,
      category: "Acciones",
      keywords: ["salir", "logout", "log out"],
      perform: logout,
    });
    return items;
  }, [visibleGroups, state, navigate, logout]);

  // NAV-03: con 3+ letras, la paleta busca socios por nombre o RUT y abre
  // su cuenta corriente. Sólo con permiso de padrón.
  const puedeBuscarSocios = hasPermission(state, "socios.read");
  const fuenteSocios = useMemo<FuenteRemota | undefined>(
    () =>
      puedeBuscarSocios
        ? {
            queryKey: "paleta-socios",
            minChars: 3,
            buscar: async (texto) => {
              const socios = await listarSocios({ search: texto, only_active: false });
              return socios.slice(0, 8).map((s) => ({
                id: `socio:${s.id}`,
                label: `Socio · ${s.nombre} (${formatRut(s.rut)})`,
                description:
                  s.estado === "Activo" ? "Cuenta corriente" : `Cuenta corriente · ${s.estado}`,
                icon: Users,
                category: "Socios" as const,
                perform: () => navigate(`/socios/${s.id}/cuenta-corriente`),
              }));
            },
          }
        : undefined,
    [puedeBuscarSocios, navigate],
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Sidebar */}
      <aside
        ref={asideRef}
        id="menu-principal"
        aria-label="Menú principal"
        className={twMerge(
          "fixed inset-y-0 left-0 z-30 flex flex-col transition-[width,transform] duration-200 md:translate-x-0",
          // Sidebar claro con el logo completo de Clarke, igual que Concilia,
          // Gestión y Asambleas (decisión 2026-09-24: el logo trae "CLARKE"
          // en negro y sobre fondo oscuro sólo se podía usar la flor de lis).
          "bg-white text-neutral-700",
          "border-r border-neutral-200 shadow-xl md:shadow-none",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          // Anchos: mobile siempre 256px, desktop colapsable
          "w-64",
          sidebarCollapsed && "md:w-16",
        )}
      >
        <div
          className={twMerge(
            "flex h-[76px] shrink-0 items-center gap-2.5 border-b border-neutral-200 transition-[padding] duration-200",
            sidebarCollapsed ? "justify-center px-3" : "px-5",
          )}
        >
          {/* Expandido: logo completo + nombre de la app (como las hermanas).
              Colapsado: no cabe el logo, queda la flor de lis. */}
          {sidebarCollapsed ? (
            <img
              src="/favicon.png"
              alt="InnovaClarke SSR"
              className="h-8 w-8 shrink-0 object-contain"
            />
          ) : (
            <div className="min-w-0">
              <img
                src="/clarke-innova-logo.png"
                alt="Clarke Innova"
                className="h-10 w-auto object-contain object-left"
              />
              <div className="mt-1 truncate text-xs font-medium text-neutral-600">
                InnovaClarke SSR · Panel administrativo
              </div>
            </div>
          )}
          <button
            ref={cerrarDrawerRef}
            type="button"
            className="ml-auto grid h-8 w-8 place-items-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <nav
          ref={navRef}
          onScroll={() => setTooltip(null)}
          className={twMerge(
            "flex-1 overflow-y-auto overscroll-contain py-3",
            sidebarCollapsed ? "px-2" : "px-3",
            // Scrollbar fina y discreta (Webkit + Firefox)
            "[scrollbar-width:thin] [scrollbar-color:theme(colors.neutral.300)_transparent]",
            "[&::-webkit-scrollbar]:w-1.5",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:bg-neutral-300/70",
            "[&::-webkit-scrollbar-thumb]:rounded-full",
            "[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400",
          )}
        >
          {/* Item destacado: Inicio (dashboard contextual) */}
          <NavLink
            to="/"
            end
            onClick={() => setMobileOpen(false)}
            aria-label={sidebarCollapsed ? "Inicio" : undefined}
            onMouseEnter={(e) => tooltipHandlers?.onShow("Inicio", e.currentTarget)}
            onMouseLeave={tooltipHandlers?.onHide}
            onFocus={(e) => tooltipHandlers?.onShow("Inicio", e.currentTarget)}
            onBlur={tooltipHandlers?.onHide}
            className={({ isActive }) =>
              moduleRowClasses(isActive, false, sidebarCollapsed)
            }
          >
            <Home className="h-[18px] w-[18px] shrink-0" aria-hidden />
            {!sidebarCollapsed && <span className="truncate">Inicio</span>}
          </NavLink>

          {SECTIONS.map((section) => {
            const groups = visibleGroups.filter((g) => g.section === section.id);
            if (groups.length === 0) return null;
            return (
              <div key={section.id} role="group" aria-label={section.label}>
                {sidebarCollapsed ? (
                  <div className="mx-2 my-3 h-px bg-neutral-200" aria-hidden />
                ) : (
                  <div className="px-3 pb-1.5 pt-5 text-xs font-semibold uppercase tracking-[0.08em] text-neutral-600">
                    {section.label}
                  </div>
                )}
                <div className="space-y-0.5">
                  {groups.map((group) => (
                    <SidebarGroup
                      key={group.id}
                      group={group}
                      expanded={expandedGroup(group.id)}
                      activeLabel={activeItemLabel(group, location.pathname, location.search)}
                      badge={alertasPorGrupo.get(group.id) ?? 0}
                      onToggle={() => toggleGroup(group.id)}
                      onNavigate={() => setMobileOpen(false)}
                      sidebarCollapsed={sidebarCollapsed}
                      tooltip={tooltipHandlers}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {state?.isSuperadmin && (
            <div role="group" aria-label={SUPERADMIN_GROUP.label}>
              {sidebarCollapsed ? (
                <div className="mx-2 my-3 h-px bg-neutral-200" aria-hidden />
              ) : (
                <div className="px-3 pb-1.5 pt-5 text-xs font-semibold uppercase tracking-[0.08em] text-amber-700">
                  Console SaaS
                </div>
              )}
              <SidebarGroup
                group={SUPERADMIN_GROUP}
                expanded={expandedGroup(SUPERADMIN_GROUP.id)}
                activeLabel={activeItemLabel(SUPERADMIN_GROUP, location.pathname, location.search)}
                badge={0}
                onToggle={() => toggleGroup(SUPERADMIN_GROUP.id)}
                onNavigate={() => setMobileOpen(false)}
                sidebarCollapsed={sidebarCollapsed}
                tooltip={tooltipHandlers}
              />
            </div>
          )}
        </nav>

        {/* Pie: Administración + comité/rol, cambiar clave y colapsar.
            "Salir" vive en la barra superior; la versión, en el title. */}
        <div
          className={twMerge(
            "shrink-0 border-t border-neutral-200 py-2",
            sidebarCollapsed ? "px-2" : "px-3",
          )}
        >
          {adminGroup && (
            <div className="mb-1">
              <SidebarGroup
                group={adminGroup}
                expanded={expandedGroup(adminGroup.id)}
                activeLabel={activeItemLabel(adminGroup, location.pathname, location.search)}
                badge={0}
                onToggle={() => toggleGroup(adminGroup.id)}
                onNavigate={() => setMobileOpen(false)}
                sidebarCollapsed={sidebarCollapsed}
                tooltip={tooltipHandlers}
              />
            </div>
          )}
          <div
            className={twMerge(
              "flex items-center rounded-lg",
              sidebarCollapsed ? "flex-col gap-1" : "gap-1 bg-neutral-50 p-1.5",
            )}
          >
            {state && (
              <div
                className={twMerge(
                  "flex min-w-0 items-center gap-2.5",
                  sidebarCollapsed ? "" : "flex-1 px-1",
                )}
                title={`${empresaLabel} · ${state.role} · v0.1.0`}
              >
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">
                  {empresaInitials(empresaLabel)}
                </div>
                {!sidebarCollapsed && (
                  <div className="min-w-0 leading-tight">
                    <div className="truncate text-xs font-medium text-neutral-800">
                      {empresaLabel}
                    </div>
                    <div className="truncate text-xs capitalize text-neutral-600">
                      {state.role}
                      {state.isSuperadmin && (
                        <span className="ml-1 inline-block rounded bg-amber-100 px-1 text-xs font-semibold uppercase text-amber-800">
                          SaaS
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <NavLink
              to="/mi-clave"
              onClick={() => setMobileOpen(false)}
              className={iconButtonClasses}
              title="Cambiar mi clave"
              aria-label="Cambiar mi clave"
            >
              <KeyRound className="h-4 w-4" aria-hidden />
            </NavLink>
            {/* Toggle desktop: colapsa/expande el sidebar (icon-only ↔ full) */}
            <button
              type="button"
              onClick={toggleSidebar}
              className={twMerge(iconButtonClasses, "hidden md:grid")}
              title={sidebarCollapsed ? "Expandir menú" : "Contraer menú"}
              aria-label={sidebarCollapsed ? "Expandir menú" : "Contraer menú"}
              aria-expanded={!sidebarCollapsed}
            >
              {sidebarCollapsed ? (
                <ChevronsRight className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronsLeft className="h-4 w-4" aria-hidden />
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Tooltip del modo contraído (fuera del nav para que no lo recorte). */}
      {tooltip && (
        <div
          role="tooltip"
          className="pointer-events-none fixed left-[72px] z-50 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-md md:block"
          style={{ top: tooltip.top }}
        >
          {tooltip.label}
        </div>
      )}

      {/* Backdrop móvil */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-neutral-900/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* Topbar + main */}
      <div
        className={twMerge(
          "transition-[padding] duration-200",
          // Igual al ancho del sidebar (w-64 / md:w-16).
          sidebarCollapsed ? "md:pl-16" : "md:pl-64",
        )}
      >
        {/* Banner de modo soporte (login-as activo) */}
        {state?.viaLoginAs && (
          <div className="sticky top-0 z-40 bg-amber-500 text-amber-950 border-b border-amber-600">
            <div className="flex items-center gap-3 px-4 py-2 text-sm">
              <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
              <div className="flex-1 leading-tight">
                <span className="font-semibold">Modo soporte activo</span>
                <span className="ml-2 opacity-90">
                  Operando como admin de{" "}
                  <strong>
                    {state.empresaTargetNombre ?? `Empresa #${state.empresaId}`}
                  </strong>
                  . Cada acción queda registrada en auditoría con tu user_id.
                </span>
              </div>
              <NavLink
                to="/superadmin/empresas"
                className="shrink-0 inline-flex items-center gap-1 rounded-md bg-amber-950/10 hover:bg-amber-950/20 px-2.5 py-1 text-xs font-medium"
                title="Volver a la Console SaaS"
              >
                Volver a Console
              </NavLink>
            </div>
          </div>
        )}
        {state && !state.viaLoginAs && <AvisoAccesoOficina state={state} />}
        <header
          className={twMerge(
            "sticky z-30 bg-white/95 backdrop-blur-sm border-b border-neutral-200",
            state?.viaLoginAs || state?.empresaOrigenId !== undefined ? "top-9" : "top-0",
          )}
        >
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              ref={hamburguesaRef}
              type="button"
              className="-ml-2 grid h-11 w-11 place-items-center rounded-md text-neutral-700 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 md:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menú"
              aria-expanded={mobileOpen}
              aria-controls="menu-principal"
            >
              <Menu className="h-5 w-5" aria-hidden />
            </button>
            {/* Command palette trigger (desktop) — el atajo nativo cambia
                entre Mac (⌘ K) y Windows/Linux (Ctrl K). El observador
                preguntó si "⌘ K" era solo para Mac (efectivamente sí); ahora
                detectamos el SO y mostramos la combinación correcta. */}
            <button
              type="button"
              onClick={() => setCmdOpen(true)}
              className="hidden md:inline-flex items-center gap-2 rounded-md bg-neutral-100 hover:bg-neutral-200 px-3 py-1.5 text-sm text-neutral-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              aria-label="Buscar pantallas, socios y acciones"
              title={`Buscar pantallas, socios y acciones (${IS_MAC ? "⌘" : "Ctrl"}+K)`}
            >
              <Search className="h-3.5 w-3.5" aria-hidden />
              <span>Buscar…</span>
              <span className="ml-1 inline-flex items-center gap-0.5">
                <kbd className="font-mono px-1 py-0.5 rounded border border-neutral-300 bg-white text-xs text-neutral-600">
                  {IS_MAC ? "⌘" : "Ctrl"}
                </kbd>
                <kbd className="font-mono px-1 py-0.5 rounded border border-neutral-300 bg-white text-xs text-neutral-600">
                  K
                </kbd>
              </span>
            </button>
            {/* Mobile: trigger sólo con icono */}
            <button
              type="button"
              onClick={() => setCmdOpen(true)}
              className="grid h-11 w-11 place-items-center rounded-md text-neutral-700 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 md:hidden"
              aria-label="Buscar pantallas, socios y acciones"
            >
              <Search className="h-5 w-5" aria-hidden />
            </button>
            <div className="ml-auto flex items-center gap-3 text-sm">
              {/* Asistente IA: sólo con permiso y el comité encendido (Console). */}
              {state && hasPermission(state, "asistente.usar") && <AsistenteBoton />}
              {/* Comité activo; selector si tiene acceso de oficina a otros. */}
              {state && <ComiteSelector state={state} />}
              <button
                type="button"
                onClick={logout}
                className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                title="Cerrar sesión"
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Salir
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 py-6 2xl:px-6">
          <Outlet />
        </main>
      </div>
      <ToastContainer />
      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        items={commandItems}
        fuenteRemota={fuenteSocios}
      />
    </div>
  );
}

const iconButtonClasses =
  "grid h-8 w-8 shrink-0 place-items-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-200/70 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1";

// Fila de primer nivel (Inicio, encabezado de módulo, íconos del modo
// contraído). Activo = marfil brand-100 + texto brand-800 + filete bronce.
function moduleRowClasses(
  isActive: boolean,
  isAmber: boolean,
  collapsed: boolean,
): string {
  const base =
    "group/item relative flex w-full items-center rounded-md text-[13px] transition-colors duration-150";
  const layout = collapsed ? "h-9 justify-center px-0" : "h-9 gap-3 px-3";
  const bar =
    "before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r-full before:transition-colors";
  const stateClasses = isActive
    ? isAmber
      ? "bg-amber-50 text-amber-800 font-medium before:bg-amber-500"
      : "bg-brand-100 text-brand-800 font-medium before:bg-brand-600"
    : isAmber
      ? "text-amber-700 hover:bg-amber-50 hover:text-amber-800 before:bg-transparent"
      : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 before:bg-transparent";
  return twMerge(base, layout, bar, stateClasses, focusRing);
}

// Opción secundaria dentro de un módulo: sangría + línea guía; el activo
// reemplaza el tramo de la guía por el filete bronce.
function subItemClasses(isActive: boolean, isAmber: boolean): string {
  const base =
    "relative -ml-px flex h-8 items-center rounded-r-md border-l pl-4 pr-3 text-[13px] transition-colors duration-150";
  const stateClasses = isActive
    ? isAmber
      ? "border-amber-500 bg-amber-50 text-amber-800 font-medium"
      : "border-brand-600 bg-brand-50 text-brand-800 font-medium"
    : "border-transparent text-neutral-600 hover:border-neutral-400 hover:bg-neutral-50 hover:text-neutral-900";
  return twMerge(base, stateClasses, focusRing);
}

interface TooltipHandlers {
  onShow: (label: string, el: HTMLElement) => void;
  onHide: () => void;
}

function SidebarGroup({
  group,
  expanded,
  activeLabel,
  badge,
  onToggle,
  onNavigate,
  sidebarCollapsed,
  tooltip,
}: {
  group: NavGroup;
  expanded: boolean;
  activeLabel: string | null;
  badge: number;
  onToggle: () => void;
  onNavigate: () => void;
  sidebarCollapsed: boolean;
  tooltip: TooltipHandlers | null;
}): JSX.Element {
  const isAmber = group.tone === "amber";
  const panelId = `sidebar-grupo-${group.id}`;
  const { pathname, search } = useLocation();
  const activo = (item: NavItem): boolean =>
    itemActivo(item, group.items, pathname, search);

  // Modo contraído: sin encabezado de grupo, cada opción como ícono con
  // tooltip accesible (hover y foco) y aria-label.
  if (sidebarCollapsed) {
    return (
      <div className="space-y-0.5">
        {group.items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            aria-label={
              group.items.length === 1 ? item.label : `${item.label} (${group.label})`
            }
            aria-current={activo(item) ? "page" : undefined}
            onMouseEnter={(e) => tooltip?.onShow(item.label, e.currentTarget)}
            onMouseLeave={tooltip?.onHide}
            onFocus={(e) => tooltip?.onShow(item.label, e.currentTarget)}
            onBlur={tooltip?.onHide}
            className={moduleRowClasses(activo(item), isAmber, true)}
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
          </Link>
        ))}
      </div>
    );
  }

  const GroupIcon = group.icon;
  const containsActive = activeLabel !== null;

  // NAV-11: un grupo con un solo ítem visible es un enlace directo (un
  // desplegable con una opción es un clic de más).
  const unico = group.items.length === 1 ? group.items[0] : undefined;
  if (unico) {
    const esActivo = activo(unico);
    return (
      <Link
        to={unico.to}
        onClick={onNavigate}
        aria-current={esActivo ? "page" : undefined}
        className={moduleRowClasses(esActivo, isAmber, false)}
      >
        <GroupIcon
          className={twMerge(
            "h-[18px] w-[18px] shrink-0 transition-colors",
            isAmber
              ? "text-amber-600"
              : esActivo
                ? "text-brand-600"
                : "text-neutral-400 group-hover/item:text-neutral-600",
          )}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate text-left">{unico.label}</span>
        {badge > 0 && <BadgeAlertas badge={badge} grupo={unico.label} />}
      </Link>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={twMerge(
          moduleRowClasses(containsActive && !expanded, isAmber, false),
          containsActive && "font-medium text-neutral-900",
        )}
      >
        <GroupIcon
          className={twMerge(
            "h-[18px] w-[18px] shrink-0 transition-colors",
            isAmber
              ? "text-amber-600"
              : containsActive
                ? "text-brand-600"
                : "text-neutral-400 group-hover/item:text-neutral-600",
          )}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
        {badge > 0 && <BadgeAlertas badge={badge} grupo={group.label} />}
        <ChevronDown
          className={twMerge(
            "h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200",
            expanded ? "rotate-0" : "-rotate-90",
          )}
          aria-hidden
        />
      </button>
      <div
        id={panelId}
        className={twMerge(
          "grid transition-[grid-template-rows] duration-200 ease-out",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="mb-1 ml-[21px] mt-0.5 space-y-px border-l border-neutral-200">
            {group.items.map((item) => (
              <div key={item.to}>
                {item.subtitulo && (
                  <div
                    className="pb-0.5 pl-4 pr-3 pt-2 text-xs font-medium text-neutral-600"
                    aria-hidden={!expanded}
                  >
                    {item.subtitulo}
                  </div>
                )}
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  tabIndex={expanded ? undefined : -1}
                  aria-current={activo(item) ? "page" : undefined}
                  className={subItemClasses(activo(item), isAmber)}
                >
                  <span className="truncate">{item.label}</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BadgeAlertas({ badge, grupo }: { badge: number; grupo: string }): JSX.Element {
  return (
    <span
      className="shrink-0 rounded-full bg-rose-50 px-1.5 py-px text-xs font-semibold tabular-nums text-rose-700 ring-1 ring-inset ring-rose-200"
      title={`${badge} alertas pendientes en ${grupo}`}
    >
      {badge > 999 ? "999+" : badge.toLocaleString("es-CL")}
      <span className="sr-only"> alertas pendientes</span>
    </span>
  );
}
